import { useEffect, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Appearance, Stripe } from '@stripe/stripe-js';
import { ArrowLeft } from 'lucide-react';
import type { PublicPlan } from '@/lib/api-client';
import { getStripePromise } from '@/lib/stripe-client';

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';

function formatPriceBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function intervalLabel(interval: PublicPlan['interval']): string {
  return interval === 'year' ? '/ano' : '/mês';
}

/** Appearance do Payment Element casado com o tema glass/escuro da landing. */
const STRIPE_APPEARANCE: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: CHAMPAGNE,
    colorBackground: 'rgba(10,7,16,0.6)',
    colorText: FG,
    colorTextSecondary: MUTED,
    colorTextPlaceholder: '#6B6478',
    colorDanger: '#FCA5A5',
    fontFamily: "'Hanken Grotesk', sans-serif",
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1px solid rgba(255,255,255,0.09)',
      boxShadow: 'none',
      padding: '11px 14px',
    },
    '.Input:focus': {
      border: '1px solid rgba(232,185,106,0.5)',
      boxShadow: '0 0 0 1px rgba(232,185,106,0.3)',
    },
    '.Label': {
      fontSize: '13px',
      fontWeight: '500',
      marginBottom: '6px',
    },
    '.Tab': {
      border: '1px solid rgba(255,255,255,0.09)',
      background: 'rgba(10,7,16,0.4)',
    },
    '.Tab:hover': {
      border: '1px solid rgba(232,185,106,0.3)',
    },
    '.Tab--selected': {
      border: '1px solid rgba(232,185,106,0.5)',
      background: 'rgba(232,185,106,0.08)',
    },
    '.TabIcon, .TabLabel': {
      color: FG,
    },
  },
};

export interface PaymentConfirmedInfo {
  /**
   * `setupIntent.payment_method` (pm_...) quando mode='setup' e a
   * confirmacao terminou sem redirect. null quando mode='payment' (o
   * caller ja tem o clientSecret/subscription que precisa) ou quando o
   * SDK nao devolveu o PM expandido.
   */
  paymentMethodId: string | null;
}

export function PaymentStep({
  plan,
  clientSecret,
  mode,
  onBack,
  returnUrl,
  backLabel,
  submitLabel,
  onConfirmed,
}: {
  /** Ausente nos fluxos que nao vendem um plano (trocar cartao, retry de fatura) — nesse caso, informe submitLabel. */
  plan?: PublicPlan;
  clientSecret: string;
  mode: 'payment' | 'setup';
  onBack: () => void;
  /** URL de retorno apos confirmar (3DS/Pix/boleto exigem navegacao real). Default: /checkout/sucesso (fluxo anonimo). */
  returnUrl?: string;
  /** Rotulo do botao de voltar. Default: "Voltar e editar meus dados" (fluxo anonimo). */
  backLabel?: string;
  /** Texto do botao de submit. Default: "Assinar · R$X/mes" calculado de `plan`. */
  submitLabel?: string;
  /**
   * Quando fornecido, a confirmacao usa redirect:'if_required' — se o
   * metodo de pagamento NAO exigir navegacao (maioria dos cartoes sem
   * 3DS), o controle volta pro JS e este callback dispara em vez do
   * redirect automatico. Usado pelo fluxo autenticado (Passo B) para
   * assumir o polling pos-pagamento (ou salvar o cartao) na propria
   * pagina. Metodos que exigem redirect real (3DS/Pix/boleto) navegam
   * normalmente para returnUrl de qualquer forma, com ou sem este callback.
   */
  onConfirmed?: (info: PaymentConfirmedInfo) => void;
}) {
  // `undefined` = carregando; `null` = falhou/indisponivel; `Stripe` = pronto.
  const [stripeInstance, setStripeInstance] = useState<Stripe | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let active = true;
    getStripePromise().then((s) => {
      if (active) setStripeInstance(s);
    });
    return () => {
      active = false;
    };
  }, []);

  if (stripeInstance === undefined) {
    return (
      <PaymentCard>
        <p style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontSize: 14, color: MUTED }}>
          Carregando formulário de pagamento...
        </p>
      </PaymentCard>
    );
  }

  if (stripeInstance === null) {
    return (
      <PaymentCard>
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8,
            padding: '14px 16px',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 13.5,
            color: '#FCA5A5',
            marginBottom: 16,
          }}
        >
          Pagamento indisponível no momento. Tente novamente em instantes ou
          entre em contato com o suporte.
        </div>
        <BackButton onClick={onBack} label={backLabel} />
      </PaymentCard>
    );
  }

  return (
    <Elements
      stripe={stripeInstance}
      options={{ clientSecret, appearance: STRIPE_APPEARANCE, locale: 'pt-BR' }}
    >
      <PaymentCard>
        <PaymentForm
          plan={plan}
          mode={mode}
          onBack={onBack}
          returnUrl={returnUrl}
          backLabel={backLabel}
          submitLabel={submitLabel}
          onConfirmed={onConfirmed}
        />
      </PaymentCard>
    </Elements>
  );
}

function PaymentForm({
  plan,
  mode,
  onBack,
  returnUrl,
  backLabel,
  submitLabel,
  onConfirmed,
}: {
  plan?: PublicPlan;
  mode: 'payment' | 'setup';
  onBack: () => void;
  returnUrl?: string;
  backLabel?: string;
  submitLabel?: string;
  onConfirmed?: (info: PaymentConfirmedInfo) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMessage(null);

    const finalReturnUrl = returnUrl ?? `${window.location.origin}/checkout/sucesso`;
    const confirmParams = { return_url: finalReturnUrl };

    if (onConfirmed) {
      // Fluxo autenticado (Passo B): so navega se o metodo escolhido
      // exigir (3DS/Pix/boleto). Nos demais casos o controle volta pro
      // JS sem sair da pagina — quem chamou assume a tela (polling ou
      // salvar o novo cartao). Branches separados por `mode` (em vez de
      // um `result` unificado) porque os tipos do Stripe fazem overload
      // por literal de `redirect` — uma variavel dinamica quebraria a
      // resolucao do overload.
      if (mode === 'payment') {
        const result = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: 'if_required',
        });
        if (result.error) {
          setErrorMessage(
            result.error.message ?? 'Não foi possível confirmar o pagamento. Tente novamente.',
          );
          setSubmitting(false);
          return;
        }
        onConfirmed({ paymentMethodId: null });
        return;
      }

      const result = await stripe.confirmSetup({
        elements,
        confirmParams,
        redirect: 'if_required',
      });
      if (result.error) {
        setErrorMessage(
          result.error.message ?? 'Não foi possível confirmar o pagamento. Tente novamente.',
        );
        setSubmitting(false);
        return;
      }
      const pm = result.setupIntent.payment_method;
      onConfirmed({
        paymentMethodId: typeof pm === 'string' ? pm : (pm?.id ?? null),
      });
      return;
    }

    // Fluxo anonimo (checkout publico) — comportamento original, inalterado:
    // sempre redireciona ao return_url em caso de sucesso.
    const { error } =
      mode === 'payment'
        ? await stripe.confirmPayment({ elements, confirmParams })
        : await stripe.confirmSetup({ elements, confirmParams });

    // Se nao houver erro, o navegador ja foi redirecionado para o
    // return_url — este trecho so roda quando a confirmacao falha
    // sincronamente (ex: cartão recusado, dados invalidos).
    if (error) {
      setErrorMessage(
        error.message ?? 'Não foi possível confirmar o pagamento. Tente novamente.',
      );
      setSubmitting(false);
    }
  }

  const finalSubmitLabel =
    submitLabel ?? (plan ? `Assinar · ${formatPriceBRL(plan.priceCents)}${intervalLabel(plan.interval)}` : 'Confirmar pagamento');

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />

      {errorMessage && (
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8,
            padding: '12px 14px',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 13,
            color: '#FCA5A5',
            marginTop: 16,
          }}
        >
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        style={{
          marginTop: 20,
          background:
            !stripe || submitting
              ? 'rgba(232,185,106,0.4)'
              : `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${CHAMPAGNE_LIGHT} 100%)`,
          color: '#1A0F00',
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: '0.01em',
          border: 'none',
          borderRadius: 10,
          padding: '15px',
          cursor: !stripe || submitting ? 'not-allowed' : 'pointer',
          width: '100%',
          boxShadow: '0 4px 24px rgba(232,185,106,0.3)',
        }}
      >
        {submitting ? 'Processando...' : finalSubmitLabel}
      </button>

      <div style={{ marginTop: 14 }}>
        <BackButton onClick={onBack} disabled={submitting} label={backLabel} />
      </div>
    </form>
  );
}

function BackButton({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: "'Hanken Grotesk', sans-serif",
        fontSize: 13,
        color: MUTED,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <ArrowLeft size={14} />
      {label ?? 'Voltar e editar meus dados'}
    </button>
  );
}

function PaymentCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'rgba(27,16,41,0.95)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 16,
        padding: 'clamp(24px, 4vw, 32px)',
      }}
    >
      {children}
    </div>
  );
}
